import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchPublic, useApiRoot } from './fetchPublic';

/**
 * @typedef {Object} PublicEventImage
 * @property {string} imageUrl
 * @property {number} width
 * @property {number} height
 */

/**
 * @typedef {Object} PublicEvent
 * @property {number} id
 * @property {string} title
 * @property {string} groupSize
 * @property {string} frequency
 * @property {string} commitmentNotes
 * @property {string} site
 * @property {string} activity
 * @property {string[]} student_ages
 * @property {string[]} subject_areas
 * @property {string[]} career_experience
 * @property {string[]} date
 * @property {string[]} participation_options
 * @property {string[]} [commitment]
 * @property {string} [city]
 * @property {string} [state]
 * @property {boolean} [completed]
 * @property {PublicEventImage} [image]
 */

/**
 * Public, shareable view of an event.
 * GET /public/event/{eventId} — unauthenticated, so no auth headers.
 * Note: apiRoot already includes the /community_v3 prefix.
 *
 * @param {number|string} eventId
 */
export default function usePublicEvent(eventId) {
  const apiRoot = useApiRoot();
  const queryClient = useQueryClient();
  const url = `${apiRoot}/public/event/${eventId}`;

  const query = useQuery({
    queryKey: ['public_event', eventId],
    queryFn: () => fetchPublic(url),
    enabled: Boolean(eventId),
  });

  const refresh = useCallback(async () => {
    try {
      const data = await fetchPublic(url, { cache: 'reload' });
      queryClient.setQueryData(['public_event', eventId], data);
    } catch {
      // Best-effort: a failed refresh leaves the current data in place.
    }
  }, [url, eventId, queryClient]);

  return {
    ...query,
    event: query.data ?? null,
    refresh,
  };
}
